/**
 * @file: InspectionFlowManager.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { InspectedItemLabel, InspectionFlowSummary } from '@euler/model';
import { CommonTaskStatus, InspectionCategory } from '@euler/model/enum';
import { Inspection } from '@euler/model/task-detail';
import { dateFromValue } from '@euler/utils/datetime';
import { IInspectionManager } from '../types';
import { InspectionManagerBase } from './InspectionManagerBase';

export class InspectionFlowManager<
  T extends Inspection,
> extends InspectionManagerBase<T> {
  constructor(inspectionManager: IInspectionManager<T>) {
    super(inspectionManager.category, inspectionManager.taskManager);
  }

  dispose() {}

  async addInspectionFlow(params: {
    templateId: number;
    assignToMemberId: number;
  }) {
    const resp = await this.taskService.addTaskInspectionFlow(this.taskNo, {
      ...params,
      category: this.category,
    });
    this.handleVersionedResponse(
      resp,
      (_, { inspection, original, response: flow }) => {
        const index = original.flows.findIndex(x => x.id === flow.id);
        if (index >= 0) {
          inspection.flows[index] = flow;
        } else {
          inspection.flows.push(flow);
        }
        // Adding a flow to a finished inspection reopens it server-side; mirror
        // that here so the status pill flips without waiting for a reload.
        if (inspection.status === CommonTaskStatus.Finished) {
          inspection.status = CommonTaskStatus.InProgress;
          inspection.finishedAt = undefined;
          inspection.finishedBy = undefined;
        }
      },
    );
  }

  async beginInspectionFlow(id: string) {
    const original = this.getTargetInspection(this.cachedDetail);
    const index = original.flows.findIndex(x => x.id === id);
    if (index < 0) {
      throw new Error('Flow does not exist');
    }

    if (original.flows[index].status !== 'pending') {
      return;
    }

    const resp = await this.taskService.beginTaskInspectionFlow(
      this.taskNo,
      this.category,
      id,
    );

    this.handleVersionedResponse(resp, (_, { inspection, response }) => {
      const flow = inspection.flows[index];
      flow.status = response.status;
      flow.startedAt = response.startedAt;
      flow.updatedAt = response.updatedAt;
      if (inspection.status !== CommonTaskStatus.InProgress) {
        inspection.status = CommonTaskStatus.InProgress;
      }
    });
  }

  async finishInspectionFlow(
    id: string,
    summary: InspectionFlowSummary,
    options: {
      technicianId: number;
      technicianName: string;
      labels?: InspectedItemLabel[];
      remark?: string;
    },
  ) {
    const original = this.getTargetInspection(this.cachedDetail);
    const index = original.flows.findIndex(x => x.id === id);
    if (index < 0) {
      throw new Error('Flow does not exist');
    }

    const currentFlow = original.flows[index];

    if (currentFlow.status === 'finished') {
      throw new Error('Flow already finished');
    }

    const resp = await this.taskService.finishTaskInspectionFlow(
      this.category,
      this.taskNo,
      id,
      summary,
      options,
    );

    this.handleVersionedResponse(resp, (_, { inspection, response }) => {
      const flow = inspection.flows[index];
      flow.status = response.status;
      flow.finishedAt = response.finishedAt;
      flow.updatedAt = response.updatedAt;
      flow.finishOptions = response.finishOptions;
      flow.resultSummary = response.resultSummary;
      if (inspection.flows.every(x => x.status === 'finished')) {
        // mark the inspection as finished as well.
        inspection.status = CommonTaskStatus.Finished;
        inspection.finishedAt = dateFromValue(flow.finishedAt);
        inspection.finishedBy = options.technicianName;
      }
    });
  }

  async requestAmendInspectionFlow(id: string) {
    const original = this.getTargetInspection(this.cachedDetail);
    const index = original.flows.findIndex(x => x.id === id);
    if (index < 0) {
      throw new Error('Flow does not exist');
    }

    if (original.flows[index].status !== 'finished') {
      throw new Error('Flow not finished yet');
    }

    const resp = await this.taskService.requestAmendTaskInspectionFlow(
      this.taskNo,
      this.category,
      id,
    );

    this.handleVersionedResponse(resp, (_, { inspection, response }) => {
      const flow = inspection.flows[index];
      flow.status = response.status;
      flow.updatedAt = response.updatedAt;
      flow.finishedAt = undefined;
      flow.finishOptions = undefined;
      flow.resultSummary = undefined;
      if (inspection.status === CommonTaskStatus.Finished) {
        inspection.status = CommonTaskStatus.InProgress;
        inspection.finishedAt = undefined;
        inspection.finishedBy = undefined;
      }
    });
  }

  async updateInspectionFlowAssignee(id: string, assignee: number) {
    const original = this.getTargetInspection(this.cachedDetail);
    const index = original.flows.findIndex(x => x.id === id);
    if (index < 0) {
      throw new Error('Flow does not exist');
    }

    if (original.flows[index].status === 'finished') {
      throw new Error('Flow already finished');
    }

    const resp = await this.taskService.updateInspectionFlowAssignee(
      this.taskNo,
      this.category,
      id,
      assignee,
    );

    this.handleVersionedResponse(resp, (_, { inspection, response }) => {
      const flow = inspection.flows[index];
      flow.updatedAt = response.updatedAt;
      flow.assignedTo = response.assignedTo;
    });
  }

  async cancelInspectionFlow(id: string) {
    const original = this.getTargetInspection(this.cachedDetail);
    const index = original.flows.findIndex(x => x.id === id);
    if (index < 0) {
      throw new Error('Flow does not exist');
    }

    if (original.flows[index].status === 'finished') {
      throw new Error('Flow already finished');
    }

    const { versions, response } =
      await this.taskService.cancelTaskInspectionFlow(
        this.taskNo,
        this.category,
        id,
      );

    this.taskManager.produceWithVersions(versions, draft => {
      draft.basicInfo = response.basicInfo ?? draft.basicInfo;

      if (this.category === InspectionCategory.Pre) {
        draft.preInspection = response.preInspection ?? draft.preInspection;
      } else {
        draft.inspection = response.inspection ?? draft.inspection;
        draft.obdInspection = response.obdInspection ?? draft.obdInspection;
      }
    });
  }
}
